/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useState, useEffect } from 'react';
import Bowser from 'bowser';

interface BrowserCompatibilityResult {
  isBrowserCompatible: boolean;
  browserName?: string;
  browserVersion?: string;
  osName?: string;
}

export const useBrowserCompatibility = (): BrowserCompatibilityResult => {
  const [compatibility, setCompatibility] = useState<BrowserCompatibilityResult>({
    isBrowserCompatible: true
  });

  useEffect(() => {
    const checkCompatibility = () => {
      const browser = Bowser.getParser(window.navigator.userAgent);
      const browserInfo = browser.getBrowser();
      const osInfo = browser.getOS();

      // Define minimum version requirements, see https://reactgrid.com/docs/4.0/0-introduction/
      const minVersions: Record<string, number> = {
        'Microsoft Edge': 80,
        'Firefox': 61,
        'Chrome': 57,
        'Safari': 13.1,
        'Samsung Internet': 9,
        'Opera': 45
      };

      // Special case for iOS/iPadOS Safari
      if (osInfo.name === 'iOS' && browserInfo.name === 'Safari' && browserInfo.version) {
        setCompatibility({
          isBrowserCompatible: parseFloat(browserInfo.version) >= 13,
          browserName: browserInfo.name,
          browserVersion: browserInfo.version,
          osName: osInfo.name
        });
        return;
      }

      // Check if browser is in our supported list and meets minimum version
      if (!browserInfo.name || !browserInfo.version) {
        setCompatibility({
          isBrowserCompatible: false,
          browserName: browserInfo.name,
          browserVersion: browserInfo.version,
          osName: osInfo.name
        });
        return;
      }

      const isBrowserCompatible = browserInfo.name in minVersions && 
                          parseFloat(browserInfo.version) >= minVersions[browserInfo.name];

      setCompatibility({
        isBrowserCompatible,
        browserName: browserInfo.name,
        browserVersion: browserInfo.version,
        osName: osInfo.name
      });
    };

    checkCompatibility();
  }, []);

  return compatibility;
};
